import { produce } from 'immer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showErrorToast } from '../../../../../../common/global/helpers';
import Button from '../../../../../../common/widgets/Button';
import Checkbox from '../../../../../../common/widgets/Checkbox';
import Dialog from '../../../../../../common/widgets/Dialog';
import Input from '../../../../../../common/widgets/Input';
import { getOptionalLabel } from '../../../../helpers';
import { resources } from '../../../../resources/resources';
import { productsService } from '../../../../services/products';
import ModalAlertImg from '../../../../../../common/assets/images/merchants/modal-attention.svg';

interface DeactivateProductDialogProps {
  closeDialog: () => void;
  productId: number;
  productName: string;
  onProductSentForDeactivation: (productId: number) => void;
}

interface DeactivateProductDialogForm {
  reason?: string;
  understood: boolean;
}

export const DeactivateProductDialog = (
  props: DeactivateProductDialogProps,
) => {
  const { closeDialog, productId, productName, onProductSentForDeactivation } =
    props;
  const { t: translate } = useTranslation();
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
  const [form, setForm] = useState<DeactivateProductDialogForm>({
    understood: false,
  });

  const onSaveClick = async () => {
    setIsButtonLoading(true);
    const resp = await productsService.deactivateProduct({
      productId,
      reason: form.reason,
    });
    setIsButtonLoading(false);
    if (resp.hasErrors) {
      showErrorToast(translate(resources.errors.generic));
    } else {
      onProductSentForDeactivation(productId);
    }
  };

  return (
    <Dialog
      closeDialog={closeDialog}
      showCloseIcon
      className="dialog-m dialog-deactivate-product"
    >
      <div className="dialog-content">
        <div className="dialog-image m-dialog-img">
          <img
            src={ModalAlertImg}
            alt={translate(resources.accounts.dialog.delete.title)}
          />
        </div>
        <div className="dialog-title">
          {translate(resources.products.deactivate.dialog.title, {
            0: productName,
          })}
        </div>
        <div className="dialog-text centered mb-20">
          {translate(resources.products.deactivate.dialog.info)}
        </div>
        <Input
          label={getOptionalLabel(
            translate(resources.products.deactivate.dialog.reason.label),
            translate,
          )}
          placeholder={translate(
            resources.products.deactivate.dialog.reason.placeholder,
          )}
          value={form.reason}
          onChange={e =>
            setForm(
              produce(d => {
                d.reason = e.value;
              }),
            )
          }
        />
        <div className="m-form-check-mb-0">
          <Checkbox
            label={translate(resources.products.deactivate.dialog.understood)}
            isChecked={form.understood}
            onChange={e =>
              setForm(
                produce(d => {
                  d.understood = e.value;
                }),
              )
            }
          />
        </div>
      </div>

      <div className="dialog-actions vertical">
        <Button
          className="btn btn-solid"
          onClick={onSaveClick}
          loading={isButtonLoading}
          disabled={!form.understood}
        >
          <span className="btn-text">
            {translate(resources.products.deactivate.dialog.button)}
          </span>
        </Button>
      </div>
    </Dialog>
  );
};
